"use client";

import { useEffect, useState, useRef } from "react";
import { PressReview } from "../types";
import { Quote, ExternalLink, PenTool } from "lucide-react";
import Link from "next/link";
import ScrollAnimation from "./ScrollAnimation";
import { motion, useMotionTemplate, useMotionValue } from "framer-motion";

function PressCard({ review }: { review: PressReview }) {
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  function handleMouseMove({ currentTarget, clientX, clientY }: React.MouseEvent) {
    const { left, top } = currentTarget.getBoundingClientRect();
    mouseX.set(clientX - left);
    mouseY.set(clientY - top);
  }

  return (
    <div 
      className="relative group border border-white/10 bg-black/40 backdrop-blur-sm rounded-2xl overflow-hidden mb-6 break-inside-avoid"
      onMouseMove={handleMouseMove}
    >
      {/* Spotlight Effect Layer */}
      <motion.div
        className="pointer-events-none absolute -inset-px rounded-2xl opacity-0 transition duration-300 group-hover:opacity-100"
        style={{
          background: useMotionTemplate`
            radial-gradient(
              650px circle at ${mouseX}px ${mouseY}px,
              rgba(222, 204, 164, 0.15),
              transparent 80%
            )
          `,
        }}
      />
      
      <div className="relative p-8 h-full flex flex-col">
        {/* Background Quote Icon */}
        <Quote className="absolute top-4 right-4 w-24 h-24 text-white/5 rotate-12 -z-10 group-hover:text-accent/10 transition-colors duration-500" />

        <div className="mb-6 flex-grow">
          <p className="font-serif text-lg leading-relaxed text-gray-300 italic group-hover:text-white transition-colors duration-300">
            &quot;{review.quote}&quot;
          </p>
        </div>

        <div className="flex items-end justify-between pt-6 border-t border-white/10 mt-auto">
          <div>
            <h4 className="font-oswald text-accent text-xl uppercase tracking-wide mb-1 flex items-center gap-2">
              {review.source}
            </h4>
            <div className="flex items-center gap-2 text-sm text-gray-500 group-hover:text-gray-400 transition-colors">
              <PenTool size={12} />
              <span>{review.writer}</span>
            </div>
          </div>

          {review.link && (
            <Link 
              href={review.link} 
              target="_blank"
              className="p-3 rounded-full bg-white/5 text-accent hover:bg-accent hover:text-primary transition-all duration-300 transform group-hover:scale-110 shadow-lg"
              aria-label={`Zum Artikel von ${review.source}`}
            >
              <ExternalLink size={20} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PressSection() {
  const [reviews, setReviews] = useState<PressReview[]>([]);

  useEffect(() => {
    fetch("/api.php?action=get_events&t=" + Date.now())
      .then(res => res.json())
      .then(data => {
        if (data.press) {
          setReviews(data.press);
        }
      })
      .catch(err => console.error("Failed to load press reviews:", err));
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section className="section-block press-section px-5" id="presse">
      <ScrollAnimation animation="fade-in">
        <h2 className="text-center">Presse</h2>
      </ScrollAnimation>

      <div className="max-w-7xl mx-auto">
        <div className="columns-1 md:columns-2 lg:columns-3 gap-6 space-y-6">
          {reviews.map((review, index) => (
            <ScrollAnimation 
              key={index} 
              animation="scale-up" 
              delay={index * 100}
              className="break-inside-avoid"
            >
              <PressCard review={review} />
            </ScrollAnimation>
          ))}
        </div>
      </div>
    </section>
  );
}
